import { Card, Col, Form, FormValues, Row } from 'antd';
import { subWeeks } from 'date-fns';
import { useEffect, useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import CustomButton from '../../../../shared/components/buttons/custom-button/custom-button';
import DatePicker from '../../../../shared/components/date-picker/date-picker';
import LineChart from '../../../../shared/components/line-chart/line-chart';
import ClientUsersSelect from '../../../client-users/components/client-users-select/client-users-select';
import { useLazyGetDashboardScanRequestsQuery } from '../../api/dashboard-api';
import { dashboardStatisticsToChartData } from '../../utils/dashboard-statistics-to-chart-data';
import limitDateRange from '../../utils/limit-date-range';
import { UserSettings } from '../../../user/models/user-settings';
import { useGetUserSettingsQuery } from '../../../user/api/user-api';
import { mapDateSearchParams } from '../../../sign-requests/utils/map-date-search-params';
import { parseServerDateFormat } from '../../../../shared/utils/format-date/format-date';

const ScanRequestsGraph = (): JSX.Element => {
  const { t } = useTranslation(['dashboard', 'common']);
  const { data: userSettings } = useGetUserSettingsQuery();

  const [form] = Form.useForm<{
    clientUsers: string[];
    dateInterval: Date[];
  }>();

  const initialValues: FormValues<typeof form> = {
    clientUsers: [],
    dateInterval: [subWeeks(new Date(), 1), new Date()],
  };

  const limitClientUserSelect = () => {
    form.setFieldsValue({
      clientUsers: form.getFieldValue('clientUsers').slice(0, 20),
    });
  };

  const [getScanDashboardRequests, { data: scanRequests, isFetching }] = useLazyGetDashboardScanRequestsQuery();

  const submitForm = (formData: FormValues<typeof form>) => {
    getScanDashboardRequests({
      clientUsers: formData.clientUsers,
      ...mapDateSearchParams(formData.dateInterval.toString()),
    });
  };

  const chartData = useMemo(
    () => dashboardStatisticsToChartData(scanRequests || [], userSettings?.[UserSettings.DateFormat]),
    [scanRequests, userSettings],
  );

  useEffect(() => {
    form.submit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalSum = useMemo(
    () => (scanRequests?.[0].dataSet || []).reduce((a, b) => a + Number(b.count), 0),
    [scanRequests],
  );

  return (
    <Card
      title={t('chart.scannedDocuments')}
      extra={<Trans i18nKey="chart.total" t={t} ns="dashboard" values={{ count: totalSum }} />}
      className="dashboard-card"
    >
      <Form onFinish={submitForm} form={form} initialValues={initialValues}>
        <Row gutter={16}>
          <Col>
            <Form.Item name="dateInterval">
              <DatePicker.RangePicker
                disabledDate={limitDateRange}
                format={parseServerDateFormat(userSettings?.[UserSettings.DateFormat])}
              />
            </Form.Item>
          </Col>
          <Col span={10}>
            <Form.Item name="clientUsers">
              <ClientUsersSelect mode="multiple" onSelect={limitClientUserSelect} maxTagCount={3} />
            </Form.Item>
          </Col>
          <Col>
            <Form.Item>
              <CustomButton type="primary" htmlType="submit" loading={isFetching}>
                {t('action.filter', { ns: 'common' })}
              </CustomButton>
            </Form.Item>
          </Col>
          <Form.Item>
            <CustomButton type="textBg" htmlType="submit" onClick={() => form.resetFields()}>
              {t('action.clearFilters', { ns: 'common' })}
            </CustomButton>
          </Form.Item>
        </Row>
      </Form>
      <LineChart data={chartData} />
    </Card>
  );
};

export default ScanRequestsGraph;
